import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Database, RefreshCw, Trash2, Plus, Pencil, Check, X } from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const catColors = {
  general: 'bg-blue-50 text-blue-700',
  decision: 'bg-purple-50 text-purple-700',
  preference: 'bg-amber-50 text-amber-700',
  learning: 'bg-green-50 text-green-700',
};

const AdminTradosAgentMemory = () => {
  const [memories, setMemories] = useState([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memoryFilter, setMemoryFilter] = useState('');
  const [showAddMemory, setShowAddMemory] = useState(false);
  const [newMemory, setNewMemory] = useState({ key: '', content: '', category: 'general' });
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ content: '', category: '' });
  const [saving, setSaving] = useState(false);

  const fetchMemories = useCallback(async () => {
    setMemoriesLoading(true);
    try {
      const params = memoryFilter ? `?category=${memoryFilter}` : '';
      const res = await api.get(`/admin/trados-agent/memories${params}`);
      setMemories(res.data.memories || []);
    } catch (err) { toast.error('Failed to load memories'); }
    finally { setMemoriesLoading(false); }
  }, [memoryFilter]);

  useEffect(() => { fetchMemories(); }, [fetchMemories]);

  const addMemory = async () => {
    if (!newMemory.key.trim() || !newMemory.content.trim()) {
      toast.error('Key and content are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/trados-agent/memories', newMemory);
      toast.success('Memory saved');
      setShowAddMemory(false);
      setNewMemory({ key: '', content: '', category: 'general' });
      fetchMemories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save memory'); }
    finally { setSaving(false); }
  };

  const startEdit = (mem) => {
    setEditingKey(mem.key);
    setEditForm({ content: mem.content, category: mem.category });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditForm({ content: '', category: '' });
  };

  const saveEdit = async (key) => {
    if (!editForm.content.trim()) {
      toast.error('Content cannot be empty');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/trados-agent/memories', {
        key,
        content: editForm.content,
        category: editForm.category,
      });
      toast.success('Memory updated');
      setEditingKey(null);
      setEditForm({ content: '', category: '' });
      fetchMemories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update memory'); }
    finally { setSaving(false); }
  };

  const removeMemory = async (key) => {
    try {
      await api.delete(`/admin/trados-agent/memories/${encodeURIComponent(key)}`);
      toast.success('Memory deleted');
      fetchMemories();
    } catch (err) { toast.error('Failed to delete memory'); }
  };

  return (
    <AgentLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['', 'general', 'decision', 'preference', 'learning'].map(f => (
              <button key={f}
                onClick={() => setMemoryFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                  memoryFilter === f ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >{f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}</button>
            ))}
          </div>
          <button onClick={() => { setShowAddMemory(!showAddMemory); cancelEdit(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
            <Plus className="w-4 h-4" /> Add Memory
          </button>
        </div>

        {/* Memory stats bar */}
        {memories.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">{memories.length} memories</p>
            </div>
            <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-gray-100">
              {['general', 'decision', 'preference', 'learning'].map(cat => {
                const count = memories.filter(m => m.category === cat).length;
                if (count === 0) return null;
                const pct = (count / memories.length) * 100;
                const colors = { general: 'bg-blue-500', decision: 'bg-purple-500', preference: 'bg-amber-500', learning: 'bg-green-500' };
                return <div key={cat} className={`${colors[cat]}`} style={{ width: `${pct}%` }} title={`${cat}: ${count}`} />;
              })}
            </div>
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              {['general', 'decision', 'preference', 'learning'].map(cat => {
                const count = memories.filter(m => m.category === cat).length;
                if (count === 0) return null;
                const colors = { general: 'text-blue-600', decision: 'text-purple-600', preference: 'text-amber-600', learning: 'text-green-600' };
                return <span key={cat} className={colors[cat]}>{cat}: {count}</span>;
              })}
            </div>
          </div>
        )}

        {/* Add Memory Form */}
        {showAddMemory && (
          <div className="bg-white rounded-xl border-2 border-indigo-200 p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-900">New Memory</p>
            <input type="text" placeholder="Key (unique identifier)" value={newMemory.key}
              onChange={(e) => setNewMemory(prev => ({ ...prev, key: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            <textarea placeholder="Content" value={newMemory.content} rows={3}
              onChange={(e) => setNewMemory(prev => ({ ...prev, content: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            <div className="flex items-center gap-3">
              <select value={newMemory.category}
                onChange={(e) => setNewMemory(prev => ({ ...prev, category: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                <option value="general">General</option>
                <option value="decision">Decision</option>
                <option value="preference">Preference</option>
                <option value="learning">Learning</option>
              </select>
              <button onClick={addMemory} disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setShowAddMemory(false); setNewMemory({ key: '', content: '', category: 'general' }); }}
                className="px-4 py-2 bg-white text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Memory Cards */}
        <div className="space-y-3">
          {memoriesLoading ? (
            <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-12 text-gray-400">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading memories...
            </div>
          ) : memories.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-12 text-gray-400">
              <Database className="w-8 h-8 mb-2" />
              <p className="text-sm">No memories stored</p>
              <p className="text-xs mt-1">Add memories to help the agent remember context</p>
            </div>
          ) : (
            memories.map(mem => {
              const isEditing = editingKey === mem.key;

              if (isEditing) {
                return (
                  <div key={mem.key} className="bg-white rounded-xl border-2 border-indigo-300 p-4 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-gray-900">{mem.key}</p>
                      <span className="text-xs text-gray-400">editing</span>
                    </div>
                    <textarea value={editForm.content} rows={4}
                      onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      autoFocus />
                    <div className="flex items-center gap-3">
                      <select value={editForm.category}
                        onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                        <option value="general">General</option>
                        <option value="decision">Decision</option>
                        <option value="preference">Preference</option>
                        <option value="learning">Learning</option>
                      </select>
                      <button onClick={() => saveEdit(mem.key)} disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
                        <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={cancelEdit}
                        className="flex items-center gap-1.5 px-4 py-2 bg-white text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                        <X className="w-4 h-4" /> Cancel
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={mem.key} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900">{mem.key}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColors[mem.category] || catColors.general}`}>
                          {mem.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{mem.content}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        {mem.source && <span>Source: {mem.source}</span>}
                        {mem.updated_at && <span>Updated: {formatDate(mem.updated_at)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                      <button onClick={() => startEdit(mem)}
                        className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-indigo-50"
                        title="Edit memory">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeMemory(mem.key)}
                        className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                        title="Delete memory">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentMemory;
